/**
 * Helpery do parsowania i formatowania liczb w formacie PL.
 *
 * <p>Wartości w bazie i w API trzymamy jako liczby JS (lub stringi z kropką dziesiętną).
 * Użytkownik PL widzi/wpisuje liczby z przecinkiem dziesiętnym i spacją jako separatorem
 * tysięcy. Te helpery konwertują w obie strony.
 */

/**
 * Parsuje string do liczby. Akceptuje formaty PL ("1 234,56") i US ("1234.56").
 * Zwraca null gdy input pusty/nieparsujemy. Zwraca {@code NaN} gdy zostały nie-numeryczne znaki.
 */
export function parsePlNumber(input: string): number | null {
  if (input == null) return null
  const trimmed = String(input).trim()
  if (trimmed === '') return null
  // Usuń spacje (separator tysięcy), zamień przecinek na kropkę.
  const normalized = trimmed.replace(/\s/g, '').replace(',', '.')
  if (!/^-?\d*\.?\d*$/.test(normalized) || normalized === '' || normalized === '-' || normalized === '.') {
    return Number.NaN
  }
  const n = Number(normalized)
  return Number.isNaN(n) ? Number.NaN : n
}

/**
 * Formatuje liczbę w formacie PL z określoną ilością miejsc po przecinku.
 * 1234.5 → "1 234,5" (decimals nieokreślone → 0..N znaków znaczących)
 * 1234.5, 2 → "1 234,50"
 */
export function formatPlNumber(value: number | null | undefined, decimals?: number): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ''
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** Formatuje kwotę PLN: "1 234,56 zł". Pusta wartość → pusty string. */
export function formatCurrencyPl(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ''
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' zł'
}

/**
 * Sanitizes raw user input while typing - filters out non-numeric chars, preserves
 * one decimal separator and one leading minus. Returns the cleaned string for setting
 * back into the input value (so cursor doesn't jump).
 */
export function sanitizeNumericInput(raw: string, allowDecimal: boolean): string {
  let cleaned = raw.replace(/[^\d,.\-]/g, '')
  // Tylko jeden minus, na początku.
  cleaned = cleaned.replace(/(?!^)-/g, '')
  if (!allowDecimal) {
    return cleaned.replace(/[.,]/g, '')
  }
  // Tylko jeden separator dziesiętny - pierwszy zostaje, reszta usuwana.
  let firstSep = -1
  let result = ''
  for (let i = 0; i < cleaned.length; i++) {
    const c = cleaned[i]
    if (c === ',' || c === '.') {
      if (firstSep === -1) {
        firstSep = i
        result += c
      }
    } else {
      result += c
    }
  }
  return result
}
